/**
 * 영속 저장소 (contracts/storage-schema.md, FR-012/016/017).
 * 최고 기록·설정만 localStorage에 저장. 접근 실패(차단/시크릿/쿼터)는 인메모리 폴백으로
 * 조용히 처리하고 게임 플레이는 지속(throw 금지, 원칙 V).
 */
import type { BestRecord, Settings } from '../core/types';

const RECORDS_KEY = 'deskrage.records.v1';
const SETTINGS_KEY = 'deskrage.settings.v1';

const DEFAULT_RECORDS: BestRecord = { version: 1, bestScore: 0, bestCombo: 0 };
const DEFAULT_SETTINGS: Settings = { version: 1, shakeIntensity: 1.0, volume: 0.7 };

// 인메모리 폴백(localStorage 접근 불가 시).
let memRecords: BestRecord | null = null;
let memSettings: Settings | null = null;

function safeGet(key: string): string | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    localStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function clampInt(n: unknown, fallback: number): number {
  return typeof n === 'number' && Number.isFinite(n) ? Math.max(0, Math.floor(n)) : fallback;
}

export function loadRecords(): BestRecord {
  if (memRecords) return { ...memRecords };
  const raw = safeGet(RECORDS_KEY);
  if (raw) {
    try {
      const p = JSON.parse(raw) as Partial<BestRecord>;
      if (p && p.version === 1) {
        return {
          version: 1,
          bestScore: clampInt(p.bestScore, 0),
          bestCombo: clampInt(p.bestCombo, 0),
        };
      }
    } catch {
      /* 파싱 실패 → 기본값 */
    }
  }
  return { ...DEFAULT_RECORDS };
}

export function saveRecords(next: BestRecord): void {
  memRecords = { ...next };
  safeSet(RECORDS_KEY, JSON.stringify(next)); // 실패해도 인메모리 폴백이 유지됨(원칙 V)
}

/** 최고 기록 갱신(각 필드 monotonic). 갱신 시 저장하고 새 기록을 반환. */
export function updateBest(totalScore: number, maxCombo: number): {
  record: BestRecord;
  isNewBest: boolean;
} {
  const cur = loadRecords();
  const isNewBest = totalScore > cur.bestScore || maxCombo > cur.bestCombo;
  const record: BestRecord = {
    version: 1,
    bestScore: Math.max(cur.bestScore, Math.floor(totalScore)),
    bestCombo: Math.max(cur.bestCombo, Math.floor(maxCombo)),
  };
  if (isNewBest) saveRecords(record);
  return { record, isNewBest };
}

export function loadSettings(): Settings {
  if (memSettings) return { ...memSettings };
  const raw = safeGet(SETTINGS_KEY);
  if (raw) {
    try {
      const p = JSON.parse(raw) as Partial<Settings> & { muted?: boolean };
      if (p && p.version === 1) {
        // 구버전 마이그레이션: volume이 없으면 muted에서 유도(muted:true → 0, false → 0.7).
        const volume =
          typeof p.volume === 'number' ? clamp01(p.volume, 0.7) : p.muted ? 0 : 0.7;
        return {
          version: 1,
          shakeIntensity: clamp01(p.shakeIntensity, 1.0),
          volume,
        };
      }
    } catch {
      /* 기본값 폴백 */
    }
  }
  return { ...DEFAULT_SETTINGS };
}

export function saveSettings(next: Settings): void {
  memSettings = { ...next };
  safeSet(SETTINGS_KEY, JSON.stringify(next)); // 실패해도 인메모리 폴백이 유지됨(원칙 V)
}

function clamp01(n: unknown, fallback: number): number {
  return typeof n === 'number' && Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : fallback;
}
